import { API_ROUTES } from '@/api/apiRoutes';
import { FolderScope } from '@/enums/folder-scope.enum';
import { log } from '@/logger/logger.ts';
import { sanitizeError } from '@/utils/sanitize-error';
import { safeAxios } from '@/services/safeAxios';
import type { DashboardRecipe } from '@/types/recipes';
import type { Recipe } from '@/types/api/recipe';
import type {
  RestoreSnapshotResponse,
  SnapshotEntry,
  SnapshotListResponse,
  SnapshotResult,
} from '@/types/api/snapshot';

const logger = log.getLogger('getRecipes');

/** Matches GET /v1/recipes/{recipeId}/fonts (CustomFontResponseDto). */
export type WorkspaceCustomFontDto = {
  id: string;
  workspaceId: string;
  weight: number;
  isItalic: boolean;
  displayName: string;
  fontFamilyKey: string;
  url: string;
  originalFileName: string | null;
  isVariable: boolean;
  createdAt: string;
  updatedAt: string;
};

export const getRecipeFonts = async (recipeId: string): Promise<WorkspaceCustomFontDto[]> => {
  try {
    const response = await safeAxios.get<WorkspaceCustomFontDto[]>(API_ROUTES.recipes.fonts({ recipeId }));
    return response.data;
  } catch (error) {
    logger.error('Error fetching recipe fonts:', error);
    throw error;
  }
};

export const getUserRecipes = async (): Promise<DashboardRecipe[]> => {
  try {
    const response = await safeAxios.get<DashboardRecipe[]>(API_ROUTES.recipes.root());
    return response.data;
  } catch (error) {
    logger.error('Error fetching user recipes:', error);
    throw error;
  }
};

/** The full workflow, including its `nodes` and `edges`. Public/showcase rows are readable by anyone. */
export const getRecipe = async (id: string): Promise<Recipe> => {
  try {
    const response = await safeAxios.get<Recipe>(API_ROUTES.recipes.byId({ recipeId: id }));
    return response.data;
  } catch (error) {
    logger.error('Error fetching recipe:', error);
    throw error;
  }
};

export const getRecipePoster = async (id: string): Promise<string | undefined> => {
  try {
    const response = await safeAxios.get<{ poster?: string }>(API_ROUTES.recipes.byId({ recipeId: id }));
    return response.data.poster;
  } catch (error) {
    logger.error('Error fetching recipe poster:', error);
    return undefined;
  }
};

export const getPublicRecipes = async (): Promise<DashboardRecipe[]> => {
  try {
    const response = await safeAxios.get<DashboardRecipe[]>(API_ROUTES.recipes.public());
    return response.data;
  } catch (error) {
    logger.error('Error fetching user recipes:', error);
    throw error;
  }
};

export const getPublicTemplateRecipes = async (): Promise<DashboardRecipe[]> => {
  try {
    const response = await safeAxios.get<DashboardRecipe[]>(API_ROUTES.recipes.publicTemplates());
    return response.data;
  } catch (error) {
    logger.error('Error fetching public template recipes:', error);
    throw error;
  }
};

export const getUserSharedApps = async (): Promise<DashboardRecipe[]> => {
  try {
    const response = await safeAxios.get<DashboardRecipe[]>(API_ROUTES.recipes.shared());
    return response.data;
  } catch (error) {
    logger.error('Error fetching shared apps:', error);
    throw error;
  }
};

export const createRecipe = async (parentId?: string | null, scope?: FolderScope) => {
  try {
    const response = await safeAxios.post<DashboardRecipe>(API_ROUTES.recipes.create(), {
      ...(parentId && { folderId: parentId }),
      ...(scope && { scope }),
    });

    return response.data;
  } catch (error) {
    logger.error('Error creating recipe:', error);
    throw error;
  }
};

export const duplicateRecipe = async (id: string) => {
  try {
    const response = await safeAxios.post<DashboardRecipe>(API_ROUTES.recipes.duplicate({ recipeId: id }));
    return response.data;
  } catch (error) {
    logger.error('Error duplicating recipe:', error);
    throw error;
  }
};

export const deleteRecipe = async (id: string, isShared: boolean) => {
  try {
    if (isShared) {
      await safeAxios.post(API_ROUTES.recipes.sharedDelete({ recipeId: id }));
    } else {
      await safeAxios.delete(API_ROUTES.recipes.byId({ recipeId: id }));
    }
  } catch (error) {
    logger.error('Error deleting recipe:', error);
    throw error;
  }
};

export const renameRecipe = async (id: string, newName: string): Promise<{ recipeUpdatedAt: string }> => {
  try {
    const response = await safeAxios.put<{ recipeUpdatedAt: string }>(API_ROUTES.recipes.byId({ recipeId: id }), {
      name: newName,
    });
    return response.data;
  } catch (error) {
    logger.error('Error renaming recipe:', error);
    throw error;
  }
};

export const changeOwner = async (id: string, newOwnerId: string) => {
  try {
    await safeAxios.put(API_ROUTES.recipes.byId({ recipeId: id }), {
      owner: newOwnerId,
    });
  } catch (error) {
    logger.error('Error changing owner:', error);
    throw error;
  }
};

export const restoreWorkflows = async (workflowIds: string[]): Promise<void> => {
  try {
    await safeAxios.patch(API_ROUTES.workflows.restore(), { workflowIds });
  } catch (e) {
    logger.error('Error restoring workflows:', e);
    throw e;
  }
};

/**
 * Lists snapshots for a recipe. Pass `metadataOnly: true` to fetch just id + timestamp
 * (no S3 body fetches, no 25-cap) — useful for picker UIs that load full content lazily on selection.
 */
export const getRecipeSnapshots = async (
  recipeId: string,
  { metadataOnly = false }: { metadataOnly?: boolean } = {},
): Promise<SnapshotEntry[]> => {
  try {
    const response = await safeAxios.get<SnapshotListResponse>(API_ROUTES.recipes.snapshots({ recipeId }), {
      params: metadataOnly ? { metadataOnly: true } : undefined,
    });
    return response.data.snapshots;
  } catch (error) {
    logger.error('Error fetching recipe snapshots:', sanitizeError(error));
    throw error;
  }
};

/**
 * Fetches a single snapshot with its full content (recipe + version).
 * Pairs with the metadata-only list flow.
 */
export const getRecipeSnapshot = async (recipeId: string, snapshotId: string): Promise<SnapshotEntry> => {
  try {
    const response = await safeAxios.get<SnapshotEntry>(API_ROUTES.recipes.snapshotById({ recipeId, snapshotId }));
    return response.data;
  } catch (error) {
    logger.error('Error fetching recipe snapshot:', sanitizeError(error));
    throw error;
  }
};

/**
 * Take an ad-hoc snapshot of the recipe; backend returns a cached entry if
 * the recipe hasn't changed since the latest existing snapshot.
 * Matches Or's `POST /v1/recipes/:recipeId/snapshot` (singular).
 */
export const createRecipeSnapshot = async (recipeId: string): Promise<SnapshotResult> => {
  try {
    const response = await safeAxios.post<SnapshotResult>(API_ROUTES.recipes.snapshot({ recipeId }));
    return response.data;
  } catch (error) {
    logger.error('Error creating recipe snapshot:', sanitizeError(error));
    throw error;
  }
};

/**
 * Restore the recipe to a previous snapshot. The server takes its own
 * pre-restore snapshot first ("if changed") so the operation is reversible.
 */
export const restoreSnapshot = async (recipeId: string, snapshotId: string): Promise<RestoreSnapshotResponse> => {
  try {
    const response = await safeAxios.post<RestoreSnapshotResponse>(
      API_ROUTES.recipes.snapshotRestore({ recipeId, snapshotId }),
    );
    return response.data;
  } catch (error) {
    logger.error('Error restoring snapshot:', sanitizeError(error));
    throw error;
  }
};

/**
 * Create a new workflow seeded from a previous snapshot of the source recipe.
 * Maps to Or's `POST /v1/recipes/:recipeId/snapshots/:snapshotId/fork`.
 */
export const forkFromSnapshot = async (recipeId: string, snapshotId: string, name: string): Promise<Recipe> => {
  try {
    const response = await safeAxios.post<Recipe>(API_ROUTES.recipes.snapshotFork({ recipeId, snapshotId }), {
      name,
    });
    return response.data;
  } catch (error) {
    logger.error('Error forking from snapshot:', sanitizeError(error));
    throw error;
  }
};
